import React, { useState, useEffect } from 'react';
import {
    Box, Flex, Heading, Text, Button, Input, Table, Thead, Tbody, Tr, Th, Td,
    VStack, HStack, useToast, Card, CardBody, IconButton, Badge,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
    useDisclosure, FormControl, FormLabel, List, ListItem, ListIcon, Tag, TagLabel, TagCloseButton
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, CheckCircleIcon } from '@chakra-ui/icons';
import { FiMessageCircle, FiPlus } from 'react-icons/fi';
import attendanceService from '../services/attendanceService';
import { fetchCourses } from '../services/courseService';

const WhatsAppNamesPage = () => {
    const [courses, setCourses] = useState([]);
    const [groupSettings, setGroupSettings] = useState([]);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [newGroupName, setNewGroupName] = useState('');
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [coursesData, groupsData] = await Promise.all([
                fetchCourses(),
                attendanceService.getWhatsAppGroups()
            ]);
            setCourses(coursesData);
            setGroupSettings(groupsData);
        } catch (error) {
            toast({ title: 'خطأ في تحميل البيانات', status: 'error' });
        }
    };

    const handleOpenCourse = (course) => {
        setSelectedCourse(course);
        const existing = groupSettings.find(g => g.matier === course.name);
        setNewGroupName('');
        onOpen();
    };

    const currentGroups = selectedCourse
        ? (groupSettings.find(g => g.matier === selectedCourse.name)?.groups || [])
        : [];

    const handleAddGroup = async () => {
        if (!newGroupName.trim()) return;
        const updatedGroups = [...currentGroups, newGroupName.trim()];
        try {
            await attendanceService.updateWhatsAppGroups({
                matier: selectedCourse.name,
                groups: updatedGroups
            });
            await loadData();
            setNewGroupName('');
            toast({ title: 'تمت إضافة المجموعة', status: 'success' });
        } catch (error) {
            toast({ title: 'خطأ', description: error.message, status: 'error' });
        }
    };

    const handleDeleteGroup = async (groupToDelete) => {
        const updatedGroups = currentGroups.filter(g => g !== groupToDelete);
        try {
            await attendanceService.updateWhatsAppGroups({
                matier: selectedCourse.name,
                groups: updatedGroups
            });
            await loadData();
            toast({ title: 'تم حذف المجموعة', status: 'info' });
        } catch (error) {
            toast({ title: 'خطأ', description: error.message, status: 'error' });
        }
    };

    return (
        <Box dir="rtl" fontFamily="Tajawal, sans-serif">
            <Heading mb={6} size={{ base: "lg", md: "xl" }}>إدارة أسماء مجموعات الواتساب</Heading>
            <Card variant="outline" borderRadius="xl" overflow="hidden" boxShadow="sm">
                <Box overflowX="auto">
                    <Table variant="simple" size={{ base: "sm", md: "md" }} bg="white">
                        <Thead bg="gray.50">
                            <Tr>
                                <Th>المادة</Th>
                                <Th>مجموعات الواتساب</Th>
                                <Th>الإجراءات</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {courses.map(course => (
                                <Tr key={course._id}>
                                    <Td fontWeight="bold">{course.name}</Td>
                                    <Td>
                                        <Flex gap={2} flexWrap="wrap">
                                            {(groupSettings.find(g => g.matier === course.name)?.groups || []).map((g, i) => (
                                                <Badge key={i} colorScheme="whatsapp" p={1} borderRadius="md" fontSize="xs">
                                                    {g}
                                                </Badge>
                                            ))}
                                        </Flex>
                                    </Td>
                                    <Td>
                                        <Button
                                            size="sm"
                                            leftIcon={<FiPlus />}
                                            colorScheme="blue"
                                            onClick={() => handleOpenCourse(course)}
                                        >
                                            <Text display={{ base: 'none', md: 'inline' }}>إدارة المجموعات</Text>
                                            <Text display={{ base: 'inline', md: 'none' }}>إدارة</Text>
                                        </Button>
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </Box>
            </Card>

            <Modal isOpen={isOpen} onClose={onClose}>
                <ModalOverlay />
                <ModalContent dir="rtl">
                    <ModalHeader fontSize="lg" fontFamily="Tajawal, sans-serif">إدارة المجموعات لـ {selectedCourse?.name}</ModalHeader>
                    <ModalCloseButton left="8px" right="auto" />
                    <ModalBody>
                        <VStack spacing={4} align="stretch">
                            <FormControl>
                                <FormLabel>إضافة اسم مجموعة جديد</FormLabel>
                                <HStack>
                                    <Input
                                        value={newGroupName}
                                        onChange={(e) => setNewGroupName(e.target.value)}
                                        placeholder="مثال: فوج أ، حصة صباحية..."
                                    />
                                    <IconButton
                                        icon={<AddIcon />}
                                        colorScheme="green"
                                        onClick={handleAddGroup}
                                    />
                                </HStack>
                            </FormControl>
                            <Box>
                                <Text fontWeight="bold" mb={2}>المجموعات الحالية:</Text>
                                <VStack align="stretch" spacing={2}>
                                    {currentGroups.map((group, idx) => (
                                        <Flex key={idx} justify="space-between" align="center" p={2} bg="gray.50" borderRadius="md">
                                            <HStack>
                                                <FiMessageCircle />
                                                <Text>{group}</Text>
                                            </HStack>
                                            <IconButton
                                                size="xs"
                                                icon={<DeleteIcon />}
                                                colorScheme="red"
                                                variant="ghost"
                                                onClick={() => handleDeleteGroup(group)}
                                            />
                                        </Flex>
                                    ))}
                                    {currentGroups.length === 0 && <Text color="gray.500">لا توجد مجموعات محددة.</Text>}
                                </VStack>
                            </Box>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={onClose} colorScheme="blue">إغلاق</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default WhatsAppNamesPage;

